import router from 'next/router';
import fetchNext from '@features/api/fetchNext';
import {
  IAddressCollection,
  IBasicAddress,
  IDeliveryAddress,
  IGuestCustomer,
  IOnlinePaymentProvider,
  IPaymentTransaction,
  IInvoiceAddress,
  TTransactionType,
} from '@features/checkout/typescript';
import { TUserBillingType } from '@features/userPanel/typescript';
import { getPaymentRedirectURI } from '@utils/misc';
import { TLocale } from '@typescript/index';
import getPublicEnv from '@utils/getPublicEnv';

class NextCustomerApi {
  private endpoints = {
    createGuestAccountWithAddress: () => `customer/guest/createWithAddress`,
    createGuestAccount: () => `customer/guest`,
    createGuestAddress: () => `customer/guest/addresses/create`,
    deleteGuestAddress: (addressId: string | string[]) => `customer/guest/addresses/${addressId}/delete`,
    changeGuestAddress: (addressId: string | string[]) => `customer/guest/addresses/${addressId}/change`,
    getGuestData: (customerId: string | string[]) => `customer/${customerId}`,
    changeAndCreateAddresses: () => `customer/addresses/addAddresses`,
    createAddresses: () => `customer/addresses/create`,
    changeUserAddress: (addressId: number) => `customer/addresses/${addressId}/change`,
    getAddressesCollection: () => `customer/addresses/getCollection`,
    deleteAddress: (addressId: number) => `customer/addresses/${addressId}/delete`,
    getOrders: (customerId: string) => `customer/${customerId}/orders`,
    getOrder: (customerId: string, orderId: string) => `customer/${customerId}/orders/${orderId}`,
    getOrderPaymentDetails: (customerId: string, orderId: string) => `customer/${customerId}/orders/${orderId}/payment`,
    getOrderNote: (customerId: string, orderId: string) => `customer/${customerId}/orders/${orderId}/note`,
    createTransaction: (customerId: string, orderId: string) => `customer/${customerId}/orders/${orderId}/transactions`,
    createOrderTransaction: (orderId: string) => `order/${orderId}/transactions`,
    cancelTransaction: (customerId: string, orderId: string) =>
      `customer/${customerId}/orders/${orderId}/transactions/cancel`,
    cancelOrderTransaction: (orderId: string) => `order/${orderId}/transactions/cancel`,
    getDictionaryType: (type: string) => `checkout/dictionary?type=${type}`,
    setBNPRepayment: () => `checkout/bnpRepayment`,
    createSurvey: () => `customer/survey`,
  };

  private static getLocale() {
    const isServerSide = typeof window === 'undefined';
    if (isServerSide) return process.env.DEFAULT_LOCALE;

    const { locale } = router;
    return locale || getPublicEnv('NEXT_PUBLIC_DEFAULT_LOCALE');
  }

  public async createGuestAccount(email: string, locale: string) {
    const method = 'POST';

    const { json } = await fetchNext(
      this.endpoints.createGuestAccount(),
      { method, body: JSON.stringify({ email, locale }) },
      NextCustomerApi.getLocale(),
    );

    return json;
  }

  public async createGuestAddress(email: string, locale: string) {
    const method = 'POST';

    const { json } = await fetchNext(
      this.endpoints.createGuestAddress(),
      { method, body: JSON.stringify({ email, locale }) },
      NextCustomerApi.getLocale(),
    );

    return json;
  }

  public async getGuestData(customerId: string) {
    const method = 'GET';

    const { json } = await fetchNext(this.endpoints.getGuestData(customerId), { method }, NextCustomerApi.getLocale());

    return json;
  }

  public async createGuestAccountWithAddress(customerWithAddresses: IGuestCustomer) {
    const method = 'POST';
    const { json } = await fetchNext(
      this.endpoints.createGuestAccountWithAddress(),
      { method, body: JSON.stringify(customerWithAddresses) },
      NextCustomerApi.getLocale(),
    );

    return json;
  }

  public async changeAndCreateAddresses(addresses: IAddressCollection) {
    const method = 'POST';
    const { json } = await fetchNext(
      this.endpoints.changeAndCreateAddresses(),
      { method, body: JSON.stringify(addresses) },
      NextCustomerApi.getLocale(),
    );

    return json;
  }

  public async createAddress(address: IBasicAddress | TUserBillingType | IDeliveryAddress) {
    const method = 'POST';
    const { json } = await fetchNext(
      this.endpoints.createAddresses(),
      { method, body: JSON.stringify(address) },
      NextCustomerApi.getLocale(),
    );

    return json;
  }

  public async changeAddress(addressId: number, address: IBasicAddress | IInvoiceAddress): Promise<IBasicAddress> {
    const method = 'PUT';
    const { json } = await fetchNext(
      this.endpoints.changeUserAddress(addressId),
      { method, body: JSON.stringify(address) },
      NextCustomerApi.getLocale(),
    );

    return json;
  }

  public async getAddressesCollection(addressesIDs: string[], customerID: string, locale?: string) {
    const method = 'POST';
    const { json } = await fetchNext(
      this.endpoints.getAddressesCollection(),
      { method, body: JSON.stringify({ addressesIDs, customerID }) },
      NextCustomerApi.getLocale() || locale,
    );

    return json;
  }

  public async getOrdersCollection(customerID: string) {
    const { json } = await fetchNext(
      this.endpoints.getOrders(customerID),
      { method: 'GET' },
      NextCustomerApi.getLocale(),
    );

    return json;
  }

  public async getOrderDetails(customerID: string, orderId: string) {
    const { json } = await fetchNext(
      this.endpoints.getOrder(customerID, orderId),
      { method: 'GET' },
      NextCustomerApi.getLocale(),
    );

    return json;
  }

  public async getOrderPaymentDetails(customerId: string, orderId: string) {
    const { json } = await fetchNext(
      this.endpoints.getOrderPaymentDetails(customerId, orderId),
      { method: 'GET' },
      NextCustomerApi.getLocale(),
    );

    return json;
  }

  public async deleteUserAddress(addressId: number) {
    const method = 'DELETE';

    const { json } = await fetchNext(this.endpoints.deleteAddress(addressId), { method }, NextCustomerApi.getLocale());

    return json;
  }

  public async createTransaction(
    customerId: string | null,
    orderId: string,
    locale: string,
    paymentProvider: IOnlinePaymentProvider,
    amount: number,
    isRepayment = false,
    transactionType: TTransactionType = 'ONLINE_CHECKOUT',
  ) {
    const method = 'POST';
    const isBankTransfer = paymentProvider.paymentMethods[0] === 'BANK_TRANSFER';
    const providerPaymentMethod = paymentProvider.paymentMethods[0];
    const returnUrls = isBankTransfer
      ? {}
      : {
          returnUrl: getPaymentRedirectURI(locale, 'return', paymentProvider.provider, isRepayment, orderId),
          cancelUrl: getPaymentRedirectURI(locale, 'cancel', paymentProvider.provider, isRepayment, orderId),
        };

    const transactions: Array<IPaymentTransaction> =
      transactionType === 'ONLINE_INSTALLMENTS'
        ? [
            {
              amount,
              transactionType: 'ONLINE_INSTALLMENTS',
              transactionData: {
                installmentsTransactionData: {
                  provider: paymentProvider.provider,
                },
              },
            },
          ]
        : [
            {
              amount,
              transactionType: 'ONLINE_CHECKOUT',
              transactionData: {
                onlineCheckoutTransactionData: {
                  provider: paymentProvider.provider,
                  options: {
                    providerPaymentMethod,
                    ...returnUrls,
                  },
                },
              },
            },
          ];

    const body: { transactions: Array<IPaymentTransaction> } = { transactions };

    const url = customerId
      ? this.endpoints.createTransaction(customerId, orderId)
      : this.endpoints.createOrderTransaction(orderId);

    if (isRepayment && paymentProvider.provider === 'BNP') {
      await fetchNext(
        this.endpoints.setBNPRepayment(),
        { method, body: JSON.stringify({ repaymentBNPOrderId: orderId }) },
        NextCustomerApi.getLocale(),
      );
    }

    const { json } = await fetchNext(url, { method, body: JSON.stringify(body) }, NextCustomerApi.getLocale());

    return json;
  }

  public async cancelTransaction(customerId: string, orderId: string, transactionIds: Array<string>) {
    const method = 'POST';

    const { json } = await fetchNext(
      this.endpoints.cancelTransaction(customerId, orderId),
      { method, body: JSON.stringify({ transactionIds }) },
      NextCustomerApi.getLocale(),
    );

    return json;
  }

  public async cancelOrderTransaction(orderId: string, transactionIds: Array<string>) {
    const method = 'POST';

    const { json } = await fetchNext(
      this.endpoints.cancelOrderTransaction(orderId),
      { method, body: JSON.stringify({ transactionIds }) },
      NextCustomerApi.getLocale(),
    );

    return json;
  }

  public async getDictionaryType(type: 'COUNTRIES' | 'TAX_REGIME', locale: TLocale | null = null) {
    const { json } = await fetchNext(
      this.endpoints.getDictionaryType(type),
      { method: 'GET' },
      locale || NextCustomerApi.getLocale(),
    );

    return json;
  }

  public async createSurvey(answer: string | null, orderId: string) {
    const method = 'POST';

    const { json } = await fetchNext(
      this.endpoints.createSurvey(),
      { method, body: JSON.stringify({ answer, orderId }) },
      NextCustomerApi.getLocale(),
    );

    return json;
  }

  public async getOrderNote(ustomerId: string, orderId: string) {
    const method = 'GET';

    const { json } = await fetchNext(
      this.endpoints.getOrderNote(ustomerId, orderId),
      { method },
      NextCustomerApi.getLocale(),
    );

    return json;
  }
}

export default new NextCustomerApi();
